import React, { useState, useMemo } from 'react';
import {
  Mic,
  Play,
  Square,
  Search,
  CheckCircle2,
  Coins,
  Sparkles,
  Star,
  Globe,
  ShieldCheck,
  Radio,
} from 'lucide-react';
import { Voice, VoiceCategory } from '../../types';

const DEFAULT_CATEGORIES: VoiceCategory[] = [
  {
    id: 'cat_standard',
    name: 'Standard Voices',
    slug: 'standard',
    description: 'Fast, crisp, clear reading ideal for scripture recaps and daily announcements.',
    creditMultiplier: 1.0,
    color: 'emerald',
  },
  {
    id: 'cat_premium',
    name: 'Premium Natural',
    slug: 'premium',
    description: 'Human-grade warmth and emotional cadence for pastoral devotionals.',
    creditMultiplier: 2.0,
    color: 'indigo',
  },
  {
    id: 'cat_ultra',
    name: 'Ultra-Expressive',
    slug: 'ultra',
    description: 'Deeply resonant, hyper-realistic tone for solemn liturgies and dramatic narrative.',
    creditMultiplier: 3.5,
    color: 'amber',
  },
  {
    id: 'cat_cloned',
    name: 'Leadership Voice Clones',
    slug: 'cloned',
    description: 'Authorized, custom voice clones of your leadership team.',
    creditMultiplier: 5.0,
    color: 'purple',
  },
];

export type VoiceCatalogTab = 'library' | 'my_voices' | 'favorites';

interface VoiceSelectorProps {
  voices: Voice[];
  selectedVoiceId: string;
  onSelectVoice: (voice: Voice) => void;
  categories?: VoiceCategory[];
  onToggleFavorite?: (voiceId: string) => void;
}

export const VoiceSelector: React.FC<VoiceSelectorProps> = ({
  voices,
  selectedVoiceId,
  onSelectVoice,
  categories = DEFAULT_CATEGORIES,
  onToggleFavorite,
}) => {
  const [activeTab, setActiveTab] = useState<VoiceCatalogTab>('library');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('ALL');
  const [selectedAccent, setSelectedAccent] = useState<string>('ALL');
  const [selectedGender, setSelectedGender] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);

  // Derive unique accents across all voices
  const availableAccents = useMemo(() => {
    const accents = new Set<string>();
    voices.forEach((v) => {
      if (v.accent) accents.add(v.accent);
    });
    return Array.from(accents).sort();
  }, [voices]);

  // Tab counts
  const libraryCount = useMemo(() => voices.filter((v) => v.type === 'STOCK').length, [voices]);
  const myVoicesCount = useMemo(() => voices.filter((v) => v.type === 'CLONED').length, [voices]);
  const favoritesCount = useMemo(() => voices.filter((v) => Boolean(v.isFavorite)).length, [voices]);

  // Filter voices based on tab, category, accent, gender, and search
  const filteredVoices = useMemo(() => {
    return voices.filter((v) => {
      // 1. Tab filter
      if (activeTab === 'library' && v.type !== 'STOCK') return false;
      if (activeTab === 'my_voices' && v.type !== 'CLONED') return false;
      if (activeTab === 'favorites' && !v.isFavorite) return false;

      // 2. Category filter
      if (selectedCategoryId !== 'ALL' && v.categoryId !== selectedCategoryId) return false;

      // 3. Accent filter
      if (selectedAccent !== 'ALL' && v.accent?.toLowerCase() !== selectedAccent.toLowerCase()) return false;

      // 4. Gender filter
      if (selectedGender !== 'ALL' && v.gender !== selectedGender) return false;

      // 5. Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = v.name.toLowerCase().includes(q);
        const matchesDesc = v.description.toLowerCase().includes(q);
        const matchesCat = v.categoryName?.toLowerCase().includes(q) || false;
        const matchesSpeaker = v.speakerName?.toLowerCase().includes(q) || false;
        const matchesAccent = v.accent?.toLowerCase().includes(q) || false;
        const matchesTags = v.tags.some((t) => t.toLowerCase().includes(q));

        if (!matchesName && !matchesDesc && !matchesCat && !matchesSpeaker && !matchesAccent && !matchesTags) {
          return false;
        }
      }

      return true;
    });
  }, [voices, activeTab, selectedCategoryId, selectedAccent, selectedGender, searchQuery]);

  const togglePlaySample = (voiceId: string, sampleAudioUrl: string | undefined, e: React.MouseEvent) => {
    e.stopPropagation();
    if (playingVoiceId === voiceId) {
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
      }
      setPlayingVoiceId(null);
      return;
    }

    if (currentAudio) {
      currentAudio.pause();
    }

    if (sampleAudioUrl) {
      const audio = new Audio(sampleAudioUrl);
      setPlayingVoiceId(voiceId);
      setCurrentAudio(audio);

      audio.play().catch((err) => {
        console.warn('Audio playback error:', err);
        setTimeout(() => {
          setPlayingVoiceId((prev) => (prev === voiceId ? null : prev));
        }, 3500);
      });

      audio.onended = () => {
        setPlayingVoiceId(null);
        setCurrentAudio(null);
      };
      audio.onerror = () => {
        setPlayingVoiceId(null);
        setCurrentAudio(null);
      };
    } else {
      setPlayingVoiceId(voiceId);
      setTimeout(() => {
        setPlayingVoiceId((prev) => (prev === voiceId ? null : prev));
      }, 3500);
    }
  };

  const handleToggleFavorite = (voiceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(voiceId);
    }
  };

  // Helper for regional flag / icon
  const getAccentBadge = (accent?: string) => {
    switch (accent?.toLowerCase()) {
      case 'american':
        return '🇺🇸 American';
      case 'british':
        return '🇬🇧 British';
      case 'nigerian':
        return '🇳🇬 Nigerian';
      case 'australian':
        return '🇦🇺 Australian';
      case 'spanish':
        return '🇪🇸 Spanish';
      default:
        return accent ? `🗣️ ${accent}` : '🗣️ Standard';
    }
  };

  return (
    <div className="space-y-4">
      {/* 3 Main Top Tabs: Voice Library | My Voices | Favorites */}
      <div className="flex items-center gap-2 p-1.5 bg-slate-100 dark:bg-slate-900/90 rounded-2xl border border-slate-200 dark:border-slate-800">
        <button
          onClick={() => setActiveTab('library')}
          className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
            activeTab === 'library'
              ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm border border-slate-200/80 dark:border-slate-700'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          <Radio className="w-3.5 h-3.5" />
          <span>Voice Library</span>
          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 font-mono">
            {libraryCount}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('my_voices')}
          className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
            activeTab === 'my_voices'
              ? 'bg-white dark:bg-slate-800 text-amber-600 dark:text-amber-400 shadow-sm border border-slate-200/80 dark:border-slate-700'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          <Mic className="w-3.5 h-3.5" />
          <span>My Voices (Clones)</span>
          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 font-mono">
            {myVoicesCount}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('favorites')}
          className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
            activeTab === 'favorites'
              ? 'bg-white dark:bg-slate-800 text-amber-500 shadow-sm border border-slate-200/80 dark:border-slate-700'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
          }`}
        >
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500" />
          <span>Favorites</span>
          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 font-mono">
            {favoritesCount}
          </span>
        </button>
      </div>

      {/* Search Input & Quick Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
          <input
            type="text"
            placeholder="Search by voice name, accent, tone, or style..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-200 dark:placeholder-slate-500 transition-colors"
          />
        </div>

        {/* Accent Filter Dropdown */}
        <select
          value={selectedAccent}
          onChange={(e) => setSelectedAccent(e.target.value)}
          className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-700 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="ALL">All Accents ({availableAccents.length})</option>
          {availableAccents.map((acc) => (
            <option key={acc} value={acc}>
              {acc} Accent
            </option>
          ))}
        </select>

        {/* Gender Filter Dropdown */}
        <select
          value={selectedGender}
          onChange={(e) => setSelectedGender(e.target.value)}
          className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-700 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="ALL">All Genders</option>
          <option value="MALE">Male</option>
          <option value="FEMALE">Female</option>
        </select>
      </div>

      {/* Category Filter Pills (When in Library or Favorites view) */}
      {activeTab !== 'my_voices' && (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          <button
            onClick={() => setSelectedCategoryId('ALL')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
              selectedCategoryId === 'ALL'
                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-800 dark:hover:text-slate-200 dark:hover:border-slate-700'
            }`}
          >
            <span>All Categories</span>
          </button>

          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategoryId(cat.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
                selectedCategoryId === cat.id
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                  : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-800 dark:hover:text-slate-200 dark:hover:border-slate-700'
              }`}
            >
              <span>{cat.name}</span>
              <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20 font-mono">
                {cat.creditMultiplier.toFixed(1)}x
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Voice Cards Grid */}
      {filteredVoices.length === 0 ? (
        <div className="text-center py-12 px-4 rounded-2xl border border-dashed border-slate-300 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
          <Mic className="w-8 h-8 text-slate-400 mx-auto mb-2 opacity-50" />
          <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
            {activeTab === 'favorites'
              ? 'No favorite voices starred yet'
              : activeTab === 'my_voices'
                ? 'No custom church clones found'
                : 'No voices matched your filters'}
          </h4>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
            {activeTab === 'favorites'
              ? 'Click the star icon ⭐ on any voice card in the library to add it to your quick-access favorites.'
              : 'Try clearing your search query or selecting a different accent or category.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3.5 max-h-[460px] overflow-y-auto pr-1">
          {filteredVoices.map((voice) => {
            const isSelected = selectedVoiceId === voice.id;
            const isPlaying = playingVoiceId === voice.id;
            const isStarred = Boolean(voice.isFavorite);
            const multiplier = voice.creditMultiplier || 1.0;

            return (
              <div
                key={voice.id}
                onClick={() => onSelectVoice(voice)}
                className={`p-4 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between relative group ${
                  isSelected
                    ? 'bg-indigo-50/70 border-indigo-500 shadow-sm ring-1 ring-indigo-500/40 dark:bg-indigo-950/40 dark:border-indigo-500 dark:shadow-lg dark:shadow-indigo-500/10 dark:ring-indigo-500/50'
                    : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 dark:bg-slate-900/70 dark:border-slate-800 dark:hover:border-slate-700 dark:hover:bg-slate-850/60'
                }`}
              >
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                          voice.type === 'CLONED'
                            ? 'bg-amber-500/10 text-amber-600 border border-amber-500/30 dark:bg-amber-500/20 dark:text-amber-300'
                            : 'bg-indigo-500/10 text-indigo-600 border border-indigo-500/30 dark:bg-indigo-500/20 dark:text-indigo-300'
                        }`}
                      >
                        <Mic className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                          <span>{voice.name}</span>
                          {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />}
                        </div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">{voice.speakerName}</div>
                      </div>
                    </div>

                    {/* Top Right Action Buttons: Favorite Star & Audio Play */}
                    <div className="flex items-center gap-1.5">
                      {/* Favorite Button */}
                      <button
                        onClick={(e) => handleToggleFavorite(voice.id, e)}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                          isStarred
                            ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-500 border border-amber-500/30 shadow-xs'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-amber-500 dark:bg-slate-800 dark:hover:bg-slate-700'
                        }`}
                        title={isStarred ? 'Remove from Favorites' : 'Add to Favorites'}
                      >
                        <Star className={`w-3.5 h-3.5 ${isStarred ? 'fill-amber-400 text-amber-500' : ''}`} />
                      </button>

                      {/* Audio Preview Button */}
                      <button
                        onClick={(e) => togglePlaySample(voice.id, voice.sampleAudioUrl, e)}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all cursor-pointer ${
                          isPlaying
                            ? 'bg-amber-500 text-white shadow-md'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300'
                        }`}
                        title={isPlaying ? 'Stop Preview' : 'Play Voice Preview'}
                      >
                        {isPlaying ? (
                          <Square className="w-3 h-3 fill-current" />
                        ) : (
                          <Play className="w-3 h-3 fill-current ml-0.5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-2.5 line-clamp-2 leading-relaxed">
                    {voice.description}
                  </p>

                  {/* Accents & Capabilities Badges */}
                  <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                    {/* Regional Accent Badge */}
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                      {getAccentBadge(voice.accent)}
                    </span>

                    {/* Accent Capability Badge */}
                    {voice.accentCapability === 'MULTILINGUAL_PRESERVED' ? (
                      <span
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/20 flex items-center gap-1"
                        title="Preserves speaker's unique accent across all languages"
                      >
                        <Globe className="w-2.5 h-2.5 text-blue-500" />
                        <span>Preserves Accent</span>
                      </span>
                    ) : (
                      <span
                        className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800/80 dark:text-slate-400 border border-slate-200 dark:border-slate-700 flex items-center gap-1"
                        title="Fixed to regional native dialect"
                      >
                        <span>Fixed Dialect</span>
                      </span>
                    )}

                    {/* Cloned Verification Badge */}
                    {voice.type === 'CLONED' && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 flex items-center gap-1">
                        <ShieldCheck className="w-2.5 h-2.5 text-emerald-500" />
                        <span>Church Verified</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Card Footer: Category & Multiplier badge */}
                <div className="mt-3.5 pt-2.5 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-500/20 flex items-center gap-1">
                      <Sparkles className="w-2.5 h-2.5 text-indigo-500 dark:text-indigo-400" />
                      {voice.categoryName || 'Standard'}
                    </span>

                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 flex items-center gap-1">
                      <Coins className="w-2.5 h-2.5 text-amber-500 dark:text-amber-400" />
                      {multiplier.toFixed(1)}x Credits
                    </span>
                  </div>

                  <span className="text-[10px] text-slate-500 font-medium capitalize">
                    {voice.gender.toLowerCase()}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

